import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, Pencil } from "lucide-react";
import { fmtDayKey, dayKey, fmtTime } from "@/lib/time";

const field = "w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-600";

export const TimeEditRow = ({ t, why, open, onOpen, onSaved, saved }) => {
  const [info, setInfo] = useState(null);
  const [pickup, setPickup] = useState("");
  const [appt, setAppt] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || info) return;
    fleetApi.get(`/trips/${t.id}/time-changes`).then((r) => {
      setInfo(r.data); setPickup(r.data.pickup_local || ""); setAppt(r.data.appointment_local || "");
    }).catch((e) => { setInfo({}); toast.error(fleetError(e, "Couldn't load this trip's times.")); });
  }, [open, info, t.id]);

  const save = async () => {
    if (!/^\d{2}:\d{2}$/.test(pickup)) { toast.error("Enter a pickup time."); return; }
    if (reason.trim().length < 3) { toast.error("Please give a reason."); return; }
    setBusy(true);
    try {
      const body = { pickup_time: pickup, reason: reason.trim() };
      if (appt && appt !== (info?.appointment_local || "")) body.appointment_time = appt;
      await fleetApi.post(`/trips/${t.id}/reschedule`, body);
      toast.success(`${t.rider_name}: pickup moved to ${pickup}.`);
      onSaved(t.id, pickup);
    } catch (e) { toast.error(e.response?.status === 403 ? "You don't have permission to change trip times." : fleetError(e, "Time change failed.")); }
    finally { setBusy(false); }
  };

  return (
    <li className={`px-5 py-4 ${open ? "bg-slate-900/70" : ""}`} data-testid={`time-fix-row-${t.id}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white">
            <Link to={`/portal/trips/${t.id}`} className="hover:text-blue-300 hover:underline">{t.rider_name}</Link>
            <span className="ml-2 font-mono-data text-[11px] font-normal text-slate-500">{t.external_trip_id} · {t.scheduled_pickup ? fmtDayKey(dayKey(t.scheduled_pickup)) : ""} · {saved || info?.pickup_local || fmtTime(t.scheduled_pickup)}</span>
          </p>
          <p className="truncate text-xs text-slate-400">{t.pickup_address} → {t.dropoff_name || t.dropoff_address}</p>
          {why && !saved && <p className="mt-0.5 text-xs text-amber-300" data-testid={`time-fix-why-${t.id}`}>{why}</p>}
        </div>
        {saved ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400" data-testid={`time-fix-saved-${t.id}`}><CheckCircle2 className="h-4 w-4" /> Saved · {saved}</span>
        ) : !open && (
          <Button size="sm" variant="outline" onClick={() => onOpen(t.id)} className="gap-1.5 border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white" data-testid={`time-fix-edit-${t.id}`}>
            <Pencil className="h-3.5 w-3.5" /> Edit time
          </Button>
        )}
      </div>
      {open && !saved && (
        !info ? <div className="py-4"><Loader2 className="h-5 w-5 animate-spin text-blue-400" /></div> : (
          <div className="mt-3 grid gap-3 sm:grid-cols-[140px_140px_1fr_auto] sm:items-end">
            <div><label className="mb-1 block text-[11px] uppercase tracking-wider text-slate-400">Pickup (was {info.pickup_local || "—"})</label>
              <input type="time" value={pickup} onChange={(e) => setPickup(e.target.value)} className={field} data-testid={`time-fix-pickup-${t.id}`} /></div>
            <div><label className="mb-1 block text-[11px] uppercase tracking-wider text-slate-400">Appointment</label>
              <input type="time" value={appt} onChange={(e) => setAppt(e.target.value)} className={field} data-testid={`time-fix-appt-${t.id}`} /></div>
            <div><label className="mb-1 block text-[11px] uppercase tracking-wider text-slate-400">Reason</label>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Rider asked for later pickup" className={field} data-testid={`time-fix-reason-${t.id}`}
                onKeyDown={(e) => { if (e.key === "Enter") save(); }} /></div>
            <Button onClick={save} disabled={busy || reason.trim().length < 3 || pickup === (info.pickup_local || "") && appt === (info.appointment_local || "")}
              className="gap-1.5 bg-blue-700 text-white hover:bg-blue-800" data-testid={`time-fix-save-${t.id}`}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save & next
            </Button>
          </div>
        )
      )}
    </li>
  );
};
